import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from '@mui/material'
import { ChevronLeft, ChevronRight, FolderRounded, StorageRounded } from '@mui/icons-material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDatabasePendingApprovalCounts } from '@src/tools/anonymizer/api/container-status-api'
import { useContainers } from '@src/tools/anonymizer/api/field-config-api'

interface ContainerSidebarProps {
  selectedContainer: string | null
  onSelectContainer: (dbName: string, containerName: string) => void
  onBackToDatabases: () => void
}

const ContainerSidebar = ({ selectedContainer, onSelectContainer, onBackToDatabases }: ContainerSidebarProps) => {
  const { t } = useTranslation()
  const [selectedDb, setSelectedDb] = useState<string | null>(null)
  const [databaseSummaries, loadingDbs] = useDatabasePendingApprovalCounts()
  const [containers, loadingContainers] = useContainers(selectedDb)

  const handleSelectDb = (dbName: string) => {
    setSelectedDb(dbName)
  }

  const handleBack = () => {
    setSelectedDb(null)
    onBackToDatabases()
  }

  const handleSelectContainer = (containerName: string) => {
    if (selectedDb) {
      onSelectContainer(selectedDb, containerName)
    }
  }

  return (
    <Box sx={{ width: 450, borderRight: 1, borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          width: 900,
          height: '100%',
          transition: 'transform 300ms ease',
          transform: selectedDb ? 'translateX(-450px)' : 'translateX(0)',
        }}
      >
        {/* Panel 1: Database list */}
        <Box sx={{ width: 450, minWidth: 450, overflow: 'auto', height: '100%' }}>
          <List dense>
            <ListSubheader>{t('anonymizer.databases')}</ListSubheader>
            {loadingDbs && <CircularProgress size={24} sx={{ ml: 2 }} />}
            {databaseSummaries.map((db) => (
              <ListItemButton key={db.dbName} onClick={() => handleSelectDb(db.dbName)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <StorageRounded
                    fontSize="small"
                    color={db.totalPendingApproval > 0 ? 'primary' : 'disabled'}
                  />
                </ListItemIcon>
                <ListItemText primary={db.dbName} />
                <Chip
                  size="small"
                  label={db.totalPendingApproval > 0 ? db.totalPendingApproval : 'OK'}
                  color={db.totalPendingApproval > 0 ? 'error' : 'success'}
                  sx={{ borderRadius: '12px', fontWeight: 600, minWidth: 32 }}
                />
                <ChevronRight fontSize="small" sx={{ ml: 0.5, color: 'text.disabled' }} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Panel 2: Container list */}
        <Box sx={{ width: 450, minWidth: 450, overflow: 'auto', height: '100%' }}>
          <List dense>
            <ListItemButton onClick={handleBack} sx={{ color: 'primary.main' }}>
              <ChevronLeft fontSize="small" sx={{ mr: 0.5 }} />
              <ListItemText
                primary={t('anonymizer.databases')}
                slotProps={{ primary: { color: 'primary', fontSize: '0.875rem' } }}
              />
            </ListItemButton>
            <ListSubheader>
              {selectedDb} — {t('anonymizer.containers')}
            </ListSubheader>
            {loadingContainers && <CircularProgress size={24} sx={{ ml: 2 }} />}
            {containers.map((container) => (
              <ListItemButton
                key={container.containerName}
                selected={selectedContainer === container.containerName}
                onClick={() => handleSelectContainer(container.containerName)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderRounded
                    fontSize="small"
                    color={container.pendingApproval > 0 ? 'primary' : 'disabled'}
                  />
                </ListItemIcon>
                <ListItemText primary={container.containerName} />
                <Chip
                  size="small"
                  label={
                    container.isDisabled
                      ? t('anonymizer.blockStatus.off')
                      : container.pendingApproval > 0
                        ? container.pendingApproval
                        : 'OK'
                  }
                  color={
                    container.isDisabled
                      ? 'info'
                      : container.pendingApproval > 0
                        ? 'error'
                        : 'success'
                  }
                  sx={{ borderRadius: '12px', fontWeight: 600, minWidth: 32 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  )
}

export default ContainerSidebar
